import { NextPage } from "next";
import router, { useRouter } from "next/router";
import { AuthGuard } from "src/components/authentication/auth-guard";
import ArticleEditorComponent from "src/components/dashboard/articleEditor";
import { DashboardLayout } from "src/components/dashboard/dashboard-layout";
import { getArticle, updateArticle } from "src/slices/articles/thunks";
import {
  selectIsCopyContent,
  selectIsDownloadContent,
  selectIsHumanizing,
  selectShowHumanizeContent,
  selectShowOriginalContent,
  setIsCopyContent,
  setIsDownloadContent,
} from "src/slices/humanize";
import { snackAdded } from "src/slices/system";
import { useDispatch, useSelector } from "src/store";

// Helper function to safely decode URL parameters
const safeDecodeURIComponent = (value: string | string[] | undefined): string => {
  if (!value) return "";
  const strValue = Array.isArray(value) ? value[0] : value;
  try {
    return decodeURIComponent(strValue);
  } catch (error) {
    console.error("Error decoding URI component:", error);
    return strValue;
  }
};

const ArticleEditor: NextPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  
  // Wait for router to be ready before accessing query params
  const isRouterReady = router.isReady;
  
  // Safely get and decode parameters
  const id = isRouterReady ? (router.query.id as string) : "";
  const title = isRouterReady 
    ? safeDecodeURIComponent(router.query.title) || "Default Title"
    : "Default Title";
  const description = isRouterReady 
    ? safeDecodeURIComponent(router.query.description) || "Default Description"
    : "Default Description";
  const hideArticle = isRouterReady ? (router.query.hideArticle === "true") : false;
  const isOptimizedMode = isRouterReady ? (router.query.isOptimizedEditor === "true") : false;

  // Get state from Redux if in regular mode
  const isHumanizing = useSelector(selectIsHumanizing);
  const showHumanizeContent = useSelector(selectShowHumanizeContent);
  const showOriginalContent = useSelector(selectShowOriginalContent);
  const isCopyContent = useSelector(selectIsCopyContent);
  const isDownloadContent = useSelector(selectIsDownloadContent);

  // Fetch content function
  const fetchContent = async (id: string) => {
    try {
      if (!id) {
        dispatch(snackAdded({
          message: "Invalid article ID",
          type: "error",
          duration: 3000,
        }));
        return null;
      }
      
      const payload = await dispatch(getArticle({ id })).unwrap();
      if (payload?.status === 'error') {
        dispatch(snackAdded({
          message: payload.error || 'Failed to load content',
          type: "error",
          duration: 3000,
        }));
        return null;
      }
      return payload?.data;
    } catch (error) {
      console.error('Error fetching article content:', error);
      dispatch(snackAdded({
        message: 'Failed to load content. Please try again.',
        type: "error",
        duration: 3000,
      }));
      return null;
    }
  };

  // Save content function
  const saveContent = async (content: string, options: any) => {
    try {
      return await dispatch(
        updateArticle({
          id,
          content,
          isHumanized: options?.isHumanized,
          isEdited: options?.isEdited,
          charCount: options?.charCount,
          lastKnownVersion: options?.lastKnownVersion,
        })
      ).unwrap();
    } catch (error) {
      console.error('Error saving article content:', error);
      dispatch(snackAdded({
        message: 'Failed to save content. Please try again.',
        type: "error",
        duration: 3000,
      }));
      return null;
    }
  };

  return (
    <ArticleEditorComponent
      id={id}
      title={title}
      description={description}
      hideArticle={hideArticle}
      isOptimizedMode={false}
      fetchContent={fetchContent}
      saveContent={saveContent}
      additionalProps={{
        isHumanizing,
        showHumanizeContent,
        showOriginalContent,
        isCopyContent,
        isDownloadContent,
        onHumanize: async (content: string) => {},
        setIsCopyContent: (value) => dispatch(setIsCopyContent(value)),
        setIsDownloadContent: (value) => dispatch(setIsDownloadContent(value)),
      }}
    />
  );
};

ArticleEditor.getLayout = (page) => {
  return (
    <AuthGuard>
      <DashboardLayout
        isArticleEditor={true}
        title={safeDecodeURIComponent(router.query.title) || ""}
        id={(router.query.id as string) || ""}
        description={safeDecodeURIComponent(router.query.description) || ""}
        isHideArticle={router.query.hideArticle as string}
        isOptimizedEditor={false}
      >
        {page}
      </DashboardLayout>
    </AuthGuard>
  );
};

export default ArticleEditor;
